import { HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { BehaviorSubject, firstValueFrom, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { PlayerRepository } from '../infrastructure/repositories/player.repository';
import { MembershipState } from '../model/enums/membership-state';
import { PlayerPermissionType } from '../model/enums/player-permission-type';
import { StorageKeys } from '../model/enums/storage-keys';
import { AuthorizationResult, JwtPayload } from '../model/interfaces/authorization-result';
import { PlayerValidationResponse } from '../model/interfaces/player-validation-response';
import { PlayerAuthRefresh } from '../model/player-auth-refresh';
import { SimplePlayer } from '../model/simple-player';
import { AuthorizationService } from './authorization/authorization.service';
import { StorageService } from './storage.service';
import { TrackingFacadeService } from './tracking/tracking-facade.service';

declare interface eventBusInteface {
    dispatchEvent(event: CustomEvent): void;
}
declare var myEventBus: eventBusInteface;


@Injectable({
    providedIn: 'root'
})
export class PlayerService
{

    public onRequestSignout = new EventEmitter<void>();
    public onAuthorizationExpirationNotify = new EventEmitter<number>();
    public onAuthorizationNeededNotification = new EventEmitter<boolean>();
    public onPlayerLoggedOutNotification = new EventEmitter<{ preserveOrder: boolean }>();
    public onPlayerLoggedIn = new EventEmitter();

    private readonly authorizationService: AuthorizationService;
    private readonly playerRepository: PlayerRepository;
    private readonly storageService: StorageService;
    private readonly trackingFacadeService: TrackingFacadeService;
    private currentPlayer: SimplePlayer;
    private readonly router: Router;
    private dataSource: BehaviorSubject<SimplePlayer>;
    private player$: Observable<SimplePlayer>;

    private dataSourceAuthorized: BehaviorSubject<boolean>;
    private authorized$: Observable<boolean>;
    private isAuthorized = false;

    public constructor(
        authorizationService: AuthorizationService,
        repository: PlayerRepository,
        storageService: StorageService,
        trackingFacadeService: TrackingFacadeService,
        router: Router
    )
    {
        this.authorizationService = authorizationService;
        this.playerRepository = repository;
        this.storageService = storageService;
        this.trackingFacadeService = trackingFacadeService;
        this.router = router;

        this.dataSourceAuthorized = new BehaviorSubject(this.isAuthorized);
        this.authorized$ = this.dataSourceAuthorized.asObservable();
 
        this.init();
    }
    
    public validatePlayer(player: SimplePlayer): Observable<PlayerValidationResponse> {
        return from(this.playerRepository.validatePlayer(player));
    }

    public getPlayerObservable(): Observable<SimplePlayer>
    {
        if (this.player$)
        {
            return this.player$;
        }

        const cachedPlayer = this.storageService.getItem<SimplePlayer>(StorageKeys.PlayerData);
        if (cachedPlayer?.PlayerId)
        {
            const token = this.storageService.getItem<string>(StorageKeys.JWT);
            if (token)
            {
                try
                {
                    const decoded = jwt_decode<JwtPayload>(token);
                    if (decoded.sub === String(cachedPlayer.PlayerId))
                    {
                        this.currentPlayer = cachedPlayer;
                        this.player$ = of(cachedPlayer);
                        return this.player$;
                    }
                }
                catch
                {
                    // Token decode failed — fall through to HTTP call
                    console.error('Token decode failed - fall through to HTTP call');
                }
            }
        }

        this.player$ = this.playerRepository.getAuthorizedPlayer().pipe(
            tap(player => {
                if (player?.PlayerId) {
                    this.storageService.setItem(StorageKeys.PlayerData, player);
                }
            }),
            shareReplay(1)
        );

        return this.player$;
    }

    public createPlayer(player: SimplePlayer): Observable<SimplePlayer>
    {
        return this.playerRepository.createPlayer(player);
    }

    private createPlayerObservable(): void
    {
        this.dataSource = new BehaviorSubject(this.currentPlayer);
        this.player$ = this.dataSource.asObservable();
    }

    public authorize(username: string, password: string): Observable<AuthorizationResult>
    {
        return this.authorizationService.authorize(username, password).pipe(map(result =>
        {
            if (result.Status === 1)
            {
                this.setAuthorized(true);
            }
            else
            {
                this.setAuthorized(false);
            }

            return result;
        }));
    }

    public setAuthorized(isPlayerAuthorized: boolean): void
    {
        this.isAuthorized = isPlayerAuthorized;
        this.dataSourceAuthorized.next(isPlayerAuthorized);
    }

    public getAuthorizedObservable(): Observable<boolean>
    {
        return this.authorized$;
    }

    public playerLoggedIn(loginType: string)
    {
        if(myEventBus){
            myEventBus.dispatchEvent(
                new CustomEvent('login.sucess', { })
            );
        }else{
            console.debug('no eventbus found in player-service')
        }


        this.trackingFacadeService.login(loginType);
        this.onPlayerLoggedIn.emit();
    }

    public requestSignout(): void
    {
        this.onRequestSignout.emit();
    }

    public async signOut(options: { reroute: boolean, preserveOrder: boolean }): Promise<void>
    {
        await this.authorizationService.signOut();
        
        this.currentPlayer = null;
        // notify everyone observing
        this.player$ = null;
        this.setAuthorized(false);

        // notify other sevices
        this.onPlayerLoggedOutNotification.emit({ preserveOrder: options.preserveOrder });

        if (options.reroute)
        {
            window.location.href = '/log-out';
        }
    }

    public updatePlayer(player: SimplePlayer): Observable<SimplePlayer>
    {
        return this.playerRepository.updatePlayer(player).pipe(
             map( (updatedPlayer) =>
                {
                    this.currentPlayer = updatedPlayer;
                    this.player$ = null;

                    return updatedPlayer;
                }
            )
        );
    }

    public updatePlayerPermission(player: SimplePlayer): void
    {
        this.playerRepository.updatePlayerPermissions(player).subscribe((updatedPlayer) =>
        {
            // track newsletterSubscription
            const newsletterSubscription = player.Permissions.filter((elem) => elem.PermissionType === PlayerPermissionType.AllowNewsletter);
            if(newsletterSubscription.length)
            {
                this.trackingFacadeService.newzletterSubscribe(newsletterSubscription[0].Allow);
            }

            this.currentPlayer = updatedPlayer;
            this.dataSource.next(updatedPlayer);
        });
    }

    public getInventoryURLForPlayer(): string
    {
        if (this.currentPlayer)
        {
            return '/min-side/mine-lodsedler?action=openshop';
        }
        else
        {
            return '/lodder-til-salg';
        }
    }

    /**
     * Determines the membership state of the current player based on their order history.
     *
     * @param newPlayerOrderThreshold - The threshold defining the maximum number of orders
     * a player can have while still being considered a "new" customer.
     *
     * - On pages before purchase completion (e.g., checkout), use `0` — a new customer has zero prior orders.
     * - On pages after purchase completion (e.g., receipt), use `1` — a new customer has only the order just placed.
     *
     * @returns The player's membership state: `Existing`, `New`, or `Unknown` if order count is unavailable.
     */
    public async membershipState(newPlayerOrderThreshold: number = 0): Promise<MembershipState> {
      const player = await firstValueFrom(this.getPlayerObservable());
      
      const subscriptionOrdersCount = player.SubscriptionOrdersCount;
    
      if (typeof subscriptionOrdersCount !== 'number') {
        return MembershipState.Unknown;
      }
    
      return subscriptionOrdersCount > newPlayerOrderThreshold
        ? MembershipState.Existing
        : MembershipState.New;
    }

    /**
     * Attempts to refresh the player's authorization token to extend the active session.
     * 
     * Should be called proactively before long-running operations (e.g., payment processing)
     * or when the user explicitly requests to extend their session via the expiration modal.
     * 
     * If the refresh fails with a 401 (Unauthorized) or 403 (Forbidden), the session can no
     * longer be extended, so the user is redirected to /login.
     * 
     * @returns Observable<PlayerAuthRefresh> - Emits the refresh response or error if token is invalid/expired
     */
    public authRefresh(): Observable<PlayerAuthRefresh> {
      return this.playerRepository.authRefresh().pipe(
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
             window.location.href = '/log-out';
          }
          return throwError(() => error);
        })
      );
    }

    private async init(): Promise<void>
    {
      this.authorizationService.expirationNotification.subscribe(number => {
        this.onAuthorizationExpirationNotify.emit(number);
      });
      
      this.authorizationService.authorizationNeededNotification.subscribe(bool => {
        this.isAuthorized = false;
        this.currentPlayer = null;
        this.onAuthorizationNeededNotification.emit(bool);
        // notify other sevices
        this.onPlayerLoggedOutNotification.emit({ preserveOrder: false });
      });

      const isAuthorized = await this.authorizationService.isAuthorized();

      this.setAuthorized(isAuthorized);
    }
}
